import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../../config/api";
import axios from "axios";
import { errorMessage } from "../../utils/errorMessage";
import { Button } from "../../components/ui/button";
import SmartClientMatterSearch from "../../components/SmartClientMatterSearch";
import {
  ArrowLeft, Camera, Loader2, Search, RefreshCw,
  ExternalLink, AlertTriangle, Plus, X,
} from "lucide-react";
import { toast } from "sonner";
import { rowHaystack } from "../../components/filesnap/fileSnapRow";
import FileSnapTable from "../../components/filesnap/FileSnapTable";

// Local time value for the datetime-local input default.
function todayLocalDatetime() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}

// ─── shared helpers ───────────────────────────────────────────────────────────

function authHeaders() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const email =
    user.user_email || user.email || user.ms_email ||
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") || "";
  return {
    "X-Bonsai-User-Id":
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") || "",
    "X-User-Email": email,
  };
}

// ─── Main page ────────────────────────────────────────────────────────────────
//
// Global File Snap hub — every file snap across all matters the user is
// authorised to see, unifying manual file notes + attendance notes. Mirrors the
// Invoice Tracker: a single searchable/filterable list keyed off matter, client
// and fee earner, with a link through to each matter.

export default function FileSnapHub() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(""); // "" | attendance_note | manual_file_note
  // BM-H4295: fee earner and date filters, and one row open at a time.
  const [feeEarnerFilter, setFeeEarnerFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");


  const fetchSnaps = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/file-snap/list`, {
        params: { limit: 2000, sort: "date_desc" },
        headers: authHeaders(),
      });
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
    } catch (err) {
      const status = err?.response?.status;
      const reason =
        status === 401 || status === 403
          ? "Session expired. Sign in again."
          : errorMessage(err, "Failed to load File Snaps.");
      setError(reason);
      setRows([]);
      toast.error("Failed to load File Snaps");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSnaps(); }, [fetchSnaps]);

  // Add-file-note modal state (Add button in the hub header).
  // The modal collects the same fields the per-matter card does + a matter
  // picker up-front (SmartClientMatterSearch), then POSTs to the same manual
  // file-note endpoint. Backend files the PDF into the matter's Attendances
  // subfolder via hub/attendances_pdf.py.
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    matter_ref: "",
    matter_label: "",
    note_date: todayLocalDatetime(),
    note_text: "",
    party_name: "",
    duration_minutes: "",
    log_time_to_lawbillity: false,
    invoice_narrative: "",
  });
  const [saving, setSaving] = useState(false);

  const resetAddForm = useCallback(() => {
    setAddForm({
      matter_ref: "",
      matter_label: "",
      note_date: todayLocalDatetime(),
      note_text: "",
      party_name: "",
      duration_minutes: "",
      log_time_to_lawbillity: false,
      invoice_narrative: "",
    });
  }, []);

  const closeAddModal = useCallback(() => {
    if (saving) return;
    setShowAddModal(false);
    resetAddForm();
  }, [saving, resetAddForm]);

  const handleSaveNote = useCallback(async () => {
    if (!addForm.matter_ref) {
      toast.error("Pick a matter first");
      return;
    }
    if (!addForm.note_text.trim()) {
      toast.error("File Snap text is required");
      return;
    }
    if (addForm.log_time_to_lawbillity && (!addForm.duration_minutes || Number(addForm.duration_minutes) < 1)) {
      toast.error("Enter a duration of at least 1 minute to record time");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        note_date: addForm.note_date ? new Date(addForm.note_date).toISOString() : "",
        note_text: addForm.note_text.trim(),
        party_name: (addForm.party_name || "").trim(),
        duration_minutes: addForm.duration_minutes ? Number(addForm.duration_minutes) : null,
        log_time_to_lawbillity: addForm.log_time_to_lawbillity,
        invoice_narrative: (addForm.invoice_narrative || "").trim(),
      };
      const r = await axios.post(
        `${API}/matters/${encodeURIComponent(addForm.matter_ref)}/file-notes`,
        payload,
        { headers: authHeaders() },
      );
      if (r.data?.sharepoint_ok === false) {
        toast.success("File Snap saved");
        toast.error(`PDF not filed to SharePoint — ${r.data?.sharepoint_reason || "see logs"}`);
      } else {
        toast.success("File Snap saved");
      }
      if (addForm.log_time_to_lawbillity && r.data?.lawbillity_ok === false) {
        toast.error(`Time not recorded: ${r.data?.lawbillity_reason || "see logs"}`);
      }
      setShowAddModal(false);
      resetAddForm();
      fetchSnaps();
    } catch (e) {
      toast.error(errorMessage(e, "Could not save File Snap"));
    } finally {
      setSaving(false);
    }
  }, [addForm, resetAddForm, fetchSnaps]);


  const feeEarners = useMemo(
    () => [...new Set(rows.map((r) => r.fee_earner || r.fee_earner_name).filter(Boolean))].sort(),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter((r) => {
      if (typeFilter && r.type !== typeFilter) return false;
      if (feeEarnerFilter && (r.fee_earner || r.fee_earner_name) !== feeEarnerFilter) return false;
      const day = (r.date || "").slice(0, 10);
      if (dateFrom && day < dateFrom) return false;
      if (dateTo && day > dateTo) return false;
      if (words.length) {
        // Every word must match somewhere, so "smith completion" narrows.
        const hay = rowHaystack(r);
        if (!words.every((w) => hay.includes(w))) return false;
      }
      return true;
    });
  }, [rows, search, typeFilter, feeEarnerFilter, dateFrom, dateTo]);

  const anyFilter = Boolean(search || typeFilter || feeEarnerFilter || dateFrom || dateTo);
  const clearFilters = () => {
    setSearch(""); setTypeFilter(""); setFeeEarnerFilter(""); setDateFrom(""); setDateTo("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy to-navy">
      <div className="glass-nav sticky top-0 z-50 w-full border-b border-teal/15">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="text-txt-muted hover:text-white p-1">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-white flex items-center gap-2">
                <Camera className="w-5 h-5 text-teal" />
                File Snap
              </h1>
              <p className="text-xs text-txt-muted">Every file snap across all your matters — manual notes and attendance, in one view</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => setShowAddModal(true)}
              className="bg-teal hover:bg-teal/85 text-white"
              data-testid="file-snap-hub-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add file note
            </Button>
            <Button variant="outline" size="sm" onClick={fetchSnaps} className="text-gray-400 border-white/10">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-status-red/40 bg-status-red/10 px-3 py-2 text-sm text-status-red" role="alert">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">File Snaps could not be loaded.</p>
              <p className="text-xs text-status-red/80">{error}</p>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-faint" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search summary, note, matter, client, party, fee earner..."
              data-testid="file-snap-search"
              className="w-full bg-navy-card border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-[#5A6A6A] focus:outline-none focus:ring-2 focus:ring-teal/40"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="file-snap-type-filter"
            aria-label="Type"
            className="bg-navy-card border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-teal/40"
          >
            <option value="">All types</option>
            <option value="attendance_note">Attendance</option>
            <option value="manual_file_note">File note</option>
          </select>
          <select
            value={feeEarnerFilter}
            onChange={(e) => setFeeEarnerFilter(e.target.value)}
            data-testid="file-snap-fee-earner-filter"
            aria-label="Fee earner"
            className="bg-navy-card border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-teal/40"
          >
            <option value="">All fee earners</option>
            {feeEarners.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <label className="flex items-center gap-1 text-xs text-txt-muted">
            From
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   data-testid="file-snap-date-from"
                   className="bg-navy-card border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white" />
          </label>
          <label className="flex items-center gap-1 text-xs text-txt-muted">
            To
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   data-testid="file-snap-date-to"
                   className="bg-navy-card border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white" />
          </label>
        </div>
        <div className="flex items-center gap-3 mb-3 text-xs text-txt-muted">
          <span data-testid="file-snap-count">
            {anyFilter
              ? `Showing ${filteredRows.length} of ${rows.length}`
              : `${rows.length} entr${rows.length === 1 ? "y" : "ies"}`}
          </span>
          {anyFilter && (
            <button type="button" onClick={clearFilters} className="text-teal hover:underline"
                    data-testid="file-snap-clear-filters">
              Clear filters
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-teal animate-spin" /></div>
        ) : filteredRows.length === 0 ? (
          <div className="text-center py-16" data-testid="file-snap-empty">
            <Camera className="w-10 h-10 text-txt-faint mx-auto mb-3" />
            <p className="text-txt-muted">No File Snaps found</p>
          </div>
        ) : (
          <div className="overflow-x-auto glass-card rounded-lg border border-white/10">
            {/* BM-H4295: icon for type, a narrow matter column that links to the
                matter, and the rest of the width for the summary. Click a row to
                read the full note. The matter card uses the same table (BM-H4308). */}
            <FileSnapTable
              rows={filteredRows}
              showMatter
              actions={(r) => r.sharepoint_file_url ? (
                <a href={r.sharepoint_file_url} target="_blank" rel="noopener noreferrer"
                   className="text-teal hover:text-teal/80" aria-label="Open in SharePoint"
                   title="Open the filed note in SharePoint">
                  <ExternalLink className="w-4 h-4" />
                </a>
              ) : null}
            />
          </div>
        )}
      </div>

      {showAddModal && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center bg-black/60 backdrop-blur-sm px-4 py-6 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-label="Add file note"
          onClick={closeAddModal}
          data-testid="file-snap-hub-add-modal"
        >
          <div
            className="relative w-full max-w-2xl mt-8 rounded-lg border border-white/10 bg-slate-900 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <h3 className="text-base font-semibold text-white flex items-center gap-2">
                <Camera className="w-4 h-4 text-teal" />
                Add file note
              </h3>
              <button
                type="button"
                onClick={closeAddModal}
                disabled={saving}
                className="text-txt-muted hover:text-white disabled:opacity-40"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {/* Matter picker — smart search across contacts + matters. */}
              <SmartClientMatterSearch
                label="Matter"
                placeholder="Search client name or matter ref…"
                mattersOnly={true}
                onSelect={(r) => {
                  const ref = r?.matter_reference || "";
                  const label = r?.client_name || r?.matter_reference || r?.label || "";
                  setAddForm((f) => ({ ...f, matter_ref: ref, matter_label: label }));
                }}
              />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="text-xs text-slate-400">
                  Date
                  <input
                    type="datetime-local"
                    className="mt-1 w-full bg-slate-800 border border-white/10 rounded px-2 py-1 text-sm text-white"
                    value={addForm.note_date}
                    onChange={(e) => setAddForm((f) => ({ ...f, note_date: e.target.value }))}
                  />
                </label>
                <label className="text-xs text-slate-400">
                  Party (optional)
                  <input
                    type="text"
                    placeholder="e.g. client, opposing solicitor…"
                    className="mt-1 w-full bg-slate-800 border border-white/10 rounded px-2 py-1 text-sm text-white"
                    value={addForm.party_name}
                    onChange={(e) => setAddForm((f) => ({ ...f, party_name: e.target.value }))}
                  />
                </label>
                <label className="text-xs text-slate-400">
                  Duration (mins)
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="mt-1 w-full bg-slate-800 border border-white/10 rounded px-2 py-1 text-sm text-white"
                    value={addForm.duration_minutes}
                    onChange={(e) => setAddForm((f) => ({ ...f, duration_minutes: e.target.value }))}
                  />
                </label>
              </div>

              <label className="block text-xs text-slate-400">
                Note
                <textarea
                  rows={6}
                  className="mt-1 w-full bg-slate-800 border border-white/10 rounded px-2 py-1 text-sm text-white"
                  placeholder="What happened, what was decided, next steps…"
                  value={addForm.note_text}
                  onChange={(e) => setAddForm((f) => ({ ...f, note_text: e.target.value }))}
                  data-testid="file-snap-hub-note-text"
                />
              </label>

              <label className="flex items-center gap-2 text-xs text-slate-300">
                <input
                  type="checkbox"
                  checked={addForm.log_time_to_lawbillity}
                  onChange={(e) => setAddForm((f) => ({ ...f, log_time_to_lawbillity: e.target.checked }))}
                />
                Record time on the matter
              </label>

              {addForm.log_time_to_lawbillity && (
                <label className="block text-xs text-slate-400">
                  Invoice narrative (optional — defaults to note party/context)
                  <input
                    type="text"
                    className="mt-1 w-full bg-slate-800 border border-white/10 rounded px-2 py-1 text-sm text-white"
                    value={addForm.invoice_narrative}
                    onChange={(e) => setAddForm((f) => ({ ...f, invoice_narrative: e.target.value }))}
                  />
                </label>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/10 bg-white/[0.02]">
              <Button
                variant="outline"
                size="sm"
                onClick={closeAddModal}
                disabled={saving}
                className="border-white/15 text-slate-200"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveNote}
                disabled={saving}
                className="bg-teal hover:bg-teal/85 text-white"
                data-testid="file-snap-hub-save-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…
                  </>
                ) : (
                  "Save file note"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
